import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getMisPlantillas,
  proponerPlantilla,
  corregirPropuesta,
} from '../../services/kapsoTemplatesService';
import { EstadoBadge } from '../SettingsPage/components/templateBadges';

const MAX_BODY = 1024;

/**
 * Título y texto de una plantilla, con las variables que se pueden insertar.
 * Lo usan proponer y corregir.
 */
export const FormularioDePlantilla = ({ variables, inicial = {}, onEnviar, onCancelar, boton }) => {
  const [description, setDescription] = useState(inicial.description || '');
  const [bodyText, setBodyText] = useState(inicial.body_text || '');
  const [enviando, setEnviando] = useState(false);

  const insertar = name => setBodyText(t => `${t}{{${name}}}`);

  const enviar = async e => {
    e.preventDefault();
    setEnviando(true);
    try {
      await onEnviar({ description: description.trim(), body_text: bodyText });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={enviar} className='space-y-3'>
      <label className='block text-sm font-medium text-slate-700'>
        Título
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder='Recordatorio amable de factura'
          className='mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm'
        />
      </label>
      <label className='block text-sm font-medium text-slate-700'>
        Texto
        <textarea
          value={bodyText}
          onChange={e => setBodyText(e.target.value)}
          rows={5}
          maxLength={MAX_BODY}
          className='mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm'
        />
      </label>
      <div className='flex flex-wrap items-center gap-2 text-xs text-slate-500'>
        <span>Insertar:</span>
        {variables.map(v => (
          <button
            key={v.name}
            type='button'
            title={v.label}
            onClick={() => insertar(v.name)}
            className='rounded-lg border border-slate-200 px-2 py-1 font-mono text-slate-600 hover:bg-slate-50'
          >
            {`{{${v.name}}}`}
          </button>
        ))}
        <span className='ml-auto'>
          {bodyText.length}/{MAX_BODY}
        </span>
      </div>
      <div className='flex gap-2'>
        <button
          type='submit'
          disabled={enviando || description.trim().length < 3 || !bodyText.trim()}
          className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
        >
          {enviando ? 'Enviando…' : boton}
        </button>
        {onCancelar && (
          <button
            type='button'
            onClick={onCancelar}
            className='rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50'
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
};

/**
 * Las plantillas de WhatsApp de la empresa (06-reparto §4.2).
 *
 * La empresa propone el texto; ClaimUp lo revisa antes de mandarlo a Meta,
 * porque una plantilla mal escrita baja la calidad de SU número y puede llegar
 * a tumbarlo. Aquí ve en qué punto está cada una y corrige las que le devuelven.
 * Ver docs/ar-plantillas-propuestas-plan.md.
 *
 * Es la pestaña WhatsApp de «Plantillas» (`PlantillasPage`): el marco de la
 * página lo pone ella.
 */
const ArTemplatesPage = () => {
  const [datos, setDatos] = useState({ templates: [], variables: [] });
  const [proponiendo, setProponiendo] = useState(false);
  const [editando, setEditando] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setDatos(await getMisPlantillas());
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const proponer = async cuerpo => {
    try {
      await proponerPlantilla(cuerpo);
      toast.success('Propuesta enviada: ClaimUp la revisará antes de mandarla a Meta');
      setProponiendo(false);
      await cargar();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const corregir = key => async cuerpo => {
    try {
      await corregirPropuesta(key, cuerpo);
      toast.success('Corregida: vuelve a revisión de ClaimUp');
      setEditando(null);
      await cargar();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <p className='mb-4 text-sm text-slate-500'>
        Los mensajes con los que se abre la conversación con un deudor. Propones el texto, ClaimUp lo revisa y después
        lo aprueba Meta. Solo se envían las aprobadas. Cuándo se mandan y en qué orden se decide en Configuración.
      </p>

      <div className='mb-6 rounded-lg border border-slate-200 bg-white p-5'>
        {proponiendo ? (
          <FormularioDePlantilla
            variables={datos.variables}
            onEnviar={proponer}
            onCancelar={() => setProponiendo(false)}
            boton='Proponer a ClaimUp'
          />
        ) : (
          <button
            type='button'
            onClick={() => setProponiendo(true)}
            className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'
          >
            Proponer una plantilla
          </button>
        )}
      </div>

      <div className='space-y-3'>
        {datos.templates.length === 0 && <p className='text-sm text-slate-500'>Todavía no tienes plantillas.</p>}
        {datos.templates.map(t => (
          <div key={t.key} className='rounded-lg border border-slate-200 bg-white p-4'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='font-medium text-slate-800'>{t.description || t.key}</span>
              <EstadoBadge estado={t.approval_status} label={t.approval_label} />
              {t.editable && editando !== t.key && (
                <button
                  type='button'
                  onClick={() => setEditando(t.key)}
                  className='ml-auto rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50'
                >
                  Corregir
                </button>
              )}
            </div>
            {t.review_note && (
              <p className='mt-2 rounded-lg bg-orange-50 px-3 py-2 text-sm text-orange-800'>ClaimUp: {t.review_note}</p>
            )}
            {t.approval_status === 'rejected' && t.rejection_reason && (
              <p className='mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800'>Meta: {t.rejection_reason}</p>
            )}
            {editando === t.key ? (
              <div className='mt-3'>
                <FormularioDePlantilla
                  variables={datos.variables}
                  inicial={t}
                  onEnviar={corregir(t.key)}
                  onCancelar={() => setEditando(null)}
                  boton='Volver a proponer'
                />
              </div>
            ) : (
              <p className='mt-2 whitespace-pre-wrap text-sm text-slate-600'>{t.preview_text}</p>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default ArTemplatesPage;
